import { makeArticle, saintQuestionRelated } from "./learnFactory";

export const pageSaintSideEffects = makeArticle({
  slug: "what-are-the-side-effects-of-saint-tms",
  title: "What Are the Side Effects of SAINT® TMS? | Neuro Wellness",
  description:
    "SAINT® TMS side effects include headache, scalp discomfort, and facial twitching. Learn about common and rare side effects, how they are managed, and who should not receive SAINT®.",
  canonical: "/learn/what-are-the-side-effects-of-saint-tms",
  h1: "What Are the Side Effects of SAINT® TMS?",
  breadcrumbLabel: "SAINT® Side Effects",
  shortAnswer:
    "The most common side effects of SAINT® TMS are headache, scalp discomfort at the treatment site, and facial muscle twitching during stimulation. These effects are usually mild to moderate and tend to decrease over the first day or two. The most serious risk is seizure, which is rare. SAINT® does not cause the memory problems or sedation associated with some other treatments. People with ferromagnetic metal in the head or implanted electronic devices should not receive SAINT®.",
  keyTakeaways: [
    "Headache is the most common side effect, reported by about two-thirds of TMS patients.",
    "Scalp discomfort and facial twitching are common and usually temporary.",
    "Seizure is the most serious risk but is rare with standard parameters.",
    "SAINT® does not cause memory loss, sedation, or weight gain.",
    "People with metal implants in the head or seizure history may not be candidates.",
  ],
  sections: [
    {
      heading: "Common Side Effects",
      paragraphs: [
        "The most commonly reported side effects of SAINT® and TMS generally are headache, scalp discomfort or pain at the stimulation site, and facial muscle twitching during pulses. In the THREE-D trial, headache was reported by about two-thirds of participants. Theta burst stimulation was rated slightly more painful than standard 10 Hz stimulation, but the difference was small.",
        "These effects are usually mild to moderate and tend to decrease over the first day or two of treatment. Over-the-counter pain relievers like acetaminophen or ibuprofen are often sufficient for headaches. The treatment team can adjust coil position or intensity if discomfort persists.",
      ],
    },
    {
      heading: "Rare but Serious Risks",
      paragraphs: [
        "The most serious risk associated with TMS is seizure, which is rare — estimated at under 0.1% of sessions with standard parameters and proper screening. A history of seizures requires careful evaluation and may rule out treatment. TMS can also, rarely, trigger manic symptoms in people with bipolar disorder, which is why screening for bipolar disorder is part of the evaluation.",
        "Because accelerated protocols deliver many more pulses per day and have been studied in far fewer people than conventional TMS, rare risks are less well characterized. This is a limitation of the current evidence.",
      ],
    },
    {
      heading: "What SAINT® Does Not Cause",
      paragraphs: [
        "SAINT® does not cause the memory problems that can follow ECT. It does not cause sedation or require anesthesia. It does not cause weight gain or sexual dysfunction, which are common side effects of many antidepressant medications. It does not require any recovery time after sessions — patients can drive and resume normal activities.",
      ],
      links: [
        {
          to: "/learn/saint-vs-traditional-tms",
          text: "Compare SAINT® to other treatments",
        },
      ],
    },
    {
      heading: "Who Should Not Receive SAINT®",
      paragraphs: [
        "People with ferromagnetic metal in or near the head — such as cochlear implants, aneurysm clips, or certain stents — should not receive SAINT® because the magnetic field could move or heat the metal. Implanted electronic stimulators are also a contraindication. People who cannot safely have an MRI are not candidates. A history of seizures requires careful evaluation. SAINT® is not FDA-cleared for bipolar depression, PTSD, anxiety disorders alone, OCD, or anyone under 18.",
      ],
      links: [{ to: "/am-i-a-candidate", text: "Check your eligibility" }],
    },
  ],
  faqs: [
    {
      question: "What are the most common side effects of SAINT TMS?",
      answer:
        "The most common side effects are headache, scalp discomfort at the treatment site, and facial muscle twitching during stimulation. These are usually mild to moderate and tend to decrease over the first day or two. Over-the-counter pain relievers are often sufficient.",
    },
    {
      question: "Can SAINT TMS cause a seizure?",
      answer:
        "Seizure is the most serious risk but is rare — estimated at under 0.1% of sessions with standard parameters and proper screening. A history of seizures requires careful evaluation and may rule out treatment. The treatment team screens for seizure risk before starting SAINT®.",
    },
    {
      question: "Does SAINT cause memory problems like ECT?",
      answer:
        "No. SAINT® does not cause the memory problems that can be associated with ECT. SAINT® does not use anesthesia, does not induce a seizure, and does not require recovery time. Patients remain awake and alert throughout treatment.",
    },
    {
      question: "Does SAINT have sexual side effects or weight gain?",
      answer:
        "No. SAINT® is a non-medication treatment and does not cause the sexual dysfunction or weight gain that are common side effects of many antidepressant medications. SAINT® introduces no chemical into the body.",
    },
    {
      question: "Who should not receive SAINT TMS?",
      answer:
        "People with ferromagnetic metal in or near the head (cochlear implants, aneurysm clips, certain stents), implanted electronic stimulators, or who cannot have an MRI are not candidates. A history of seizures requires careful evaluation. SAINT® is not FDA-cleared for bipolar depression, PTSD, OCD, anxiety alone, or anyone under 18.",
    },
  ],
  sources: [
    {
      citation:
        "Blumberger DM, et al. THREE-D trial. Lancet. 2018;391(10131):1683-1692.",
      url: "https://doi.org/10.1016/S0140-6736(18)30295-2",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
  ],
  relatedLinks: saintQuestionRelated,
});
